import React, { useEffect, useMemo, useState } from "react";
import { api, formatIDR, errMsg } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Minus, X, ShoppingCart, Package, Search, Sparkles, User, UserPlus } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

export default function POS() {
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [customer, setCustomer] = useState("");
  const [payment, setPayment] = useState("cash");
  const [q, setQ] = useState("");
  const [customers, setCustomers] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [showCustomerList, setShowCustomerList] = useState(false);
  const [newCustOpen, setNewCustOpen] = useState(false);
  const [newCust, setNewCust] = useState({ name: "", phone: "", address: "", notes: "" });

  const load = () => api.get("/products").then((r) => setProducts(r.data));
  const loadCustomers = () => api.get("/customers").then((r) => setCustomers(r.data));
  useEffect(() => { load(); loadCustomers(); }, []);

  // Load suggestions when a known customer is picked
  useEffect(() => {
    const c = customer.trim();
    if (!c) { setSuggestions([]); return; }
    const match = customers.find((x) => x.name.toLowerCase() === c.toLowerCase());
    if (match) {
      api.get(`/customers/${encodeURIComponent(match.name)}/suggestions`)
        .then((r) => setSuggestions(r.data.suggestions || []));
    } else {
      setSuggestions([]);
    }
  }, [customer, customers]);

  const filteredCustomers = useMemo(() => {
    if (!customer) return customers.slice(0, 5);
    return customers.filter((c) => c.name.toLowerCase().includes(customer.toLowerCase())).slice(0, 8);
  }, [customer, customers]);

  const filtered = products.filter((p) => p.name.toLowerCase().includes(q.toLowerCase()));

  const addItem = (p) => {
    if (p.stock <= 0) return toast.error("Stok habis");
    setCart((c) => {
      const found = c.find((x) => x.product_id === p.id);
      if (found) {
        if (found.qty >= p.stock) { toast.error("Stok tidak cukup"); return c; }
        return c.map((x)=>x.product_id===p.id?{...x, qty:x.qty+1}:x);
      }
      return [...c, { product_id: p.id, product_name: p.name, unit_price: p.retail_price, qty: 1, stock: p.stock, unit: p.unit }];
    });
  };

  const updateQty = (id, delta) => setCart((c)=>c.map((x)=>{
    if (x.product_id!==id) return x;
    const q = x.qty + delta;
    if (q<=0) return null;
    if (q>x.stock) { toast.error("Stok tidak cukup"); return x; }
    return {...x, qty:q};
  }).filter(Boolean));

  const setPrice = (id, price) => setCart((c)=>c.map((x)=>x.product_id===id?{...x, unit_price:Number(price)}:x));
  const removeItem = (id) => setCart((c)=>c.filter((x)=>x.product_id!==id));

  const total = useMemo(()=>cart.reduce((s,x)=>s + x.qty * x.unit_price, 0),[cart]);

  const checkout = async () => {
    if (cart.length === 0) return toast.error("Keranjang kosong");
    try {
      await api.post("/sales", {
        customer_name: customer || "Konsumen",
        payment_method: payment,
        items: cart.map((x)=>({product_id:x.product_id, qty:x.qty, unit_price:x.unit_price})),
      });
      toast.success("Penjualan berhasil. Stok & kas terupdate!");
      setCart([]); setCustomer(""); load(); loadCustomers();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const addByProductId = (pid) => {
    const p = products.find((x) => x.id === pid);
    if (p) addItem(p);
  };

  const saveNewCustomer = async () => {
    if (!newCust.name.trim()) return toast.error("Nama wajib diisi");
    try {
      const { data } = await api.post("/customers", newCust);
      toast.success(`Konsumen "${data.name}" tersimpan`);
      setCustomer(data.name);
      setNewCustOpen(false);
      setNewCust({ name: "", phone: "", address: "", notes: "" });
      loadCustomers();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const openNewCustomer = () => {
    setNewCust({ name: customer.trim(), phone: "", address: "", notes: "" });
    setNewCustOpen(true);
    setShowCustomerList(false);
  };

  return (
    <>
      <PageHeader title="Kasir Penjualan" subtitle="Jual langsung ke konsumen. Stok & kas otomatis." testId="page-pos" />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left: product grid */}
        <div className="lg:col-span-2 space-y-4">
          <Card className="p-4 border border-[var(--hotto-border)] rounded-md">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
              <Input placeholder="Cari produk..." value={q} onChange={(e)=>setQ(e.target.value)} data-testid="input-pos-search" className="pl-9"/>
            </div>
          </Card>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {filtered.length === 0 && <div className="col-span-full text-slate-500 text-center py-12">Tidak ada produk</div>}
            {filtered.map((p) => (
              <button
                key={p.id}
                onClick={()=>addItem(p)}
                disabled={p.stock<=0}
                data-testid={`pos-product-${p.id}`}
                className="text-left border border-[var(--hotto-border)] bg-white rounded-md p-4 hover:border-[var(--hotto-primary)] hover:shadow-sm transition-[transform,box-shadow] hover:-translate-y-0.5 disabled:opacity-50"
              >
                <div className="w-9 h-9 rounded-md bg-emerald-50 flex items-center justify-center mb-2">
                  <Package className="w-4 h-4 text-[var(--hotto-primary)]"/>
                </div>
                <div className="font-medium text-sm mb-1 line-clamp-2">{p.name}</div>
                <div className="text-xs text-slate-500 mb-2">Stok: {p.stock} {p.unit}</div>
                <div className="font-mono-num font-semibold text-[var(--hotto-primary)]">{formatIDR(p.retail_price)}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Right: cart */}
        <div>
          <Card className="border border-[var(--hotto-border)] rounded-md sticky top-4">
            <div className="p-4 border-b border-[var(--hotto-border)] flex items-center gap-2">
              <ShoppingCart className="w-4 h-4"/> <span className="font-semibold" style={{fontFamily:'Outfit'}}>Keranjang</span>
              <Badge variant="secondary" className="ml-auto">{cart.length}</Badge>
            </div>
            <div className="max-h-[400px] overflow-y-auto">
              {cart.length === 0 && <div className="p-8 text-center text-slate-500 text-sm">Klik produk untuk menambahkan</div>}
              {cart.map((x)=>(
                <div key={x.product_id} className="p-3 border-b border-[var(--hotto-border)]">
                  <div className="flex items-start justify-between mb-2">
                    <div className="text-sm font-medium flex-1">{x.product_name}</div>
                    <Button size="icon" variant="ghost" className="h-6 w-6" onClick={()=>removeItem(x.product_id)}><X className="w-3 h-3"/></Button>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1">
                      <Button size="icon" variant="outline" className="h-7 w-7" onClick={()=>updateQty(x.product_id,-1)} data-testid={`cart-dec-${x.product_id}`}><Minus className="w-3 h-3"/></Button>
                      <div className="w-8 text-center font-mono-num">{x.qty}</div>
                      <Button size="icon" variant="outline" className="h-7 w-7" onClick={()=>updateQty(x.product_id,+1)} data-testid={`cart-inc-${x.product_id}`}><Plus className="w-3 h-3"/></Button>
                    </div>
                    <Input value={x.unit_price} type="number" onChange={(e)=>setPrice(x.product_id,e.target.value)} className="h-7 text-sm w-28 font-mono-num text-right"/>
                  </div>
                  <div className="text-right font-mono-num text-sm font-semibold mt-1">{formatIDR(x.qty*x.unit_price)}</div>
                </div>
              ))}
            </div>
            <div className="p-4 space-y-3 bg-slate-50/50">
              <div className="relative">
                <div className="flex items-center justify-between">
                  <Label>Nama Konsumen</Label>
                  <button
                    type="button"
                    onClick={openNewCustomer}
                    data-testid="btn-new-customer"
                    className="text-xs text-[var(--hotto-primary)] hover:underline flex items-center gap-1"
                  >
                    <UserPlus className="w-3 h-3"/> Konsumen Baru
                  </button>
                </div>
                <div className="relative">
                  <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                  <Input
                    value={customer}
                    onChange={(e)=>{setCustomer(e.target.value); setShowCustomerList(true);}}
                    onFocus={()=>setShowCustomerList(true)}
                    onBlur={()=>setTimeout(()=>setShowCustomerList(false), 200)}
                    placeholder="Ketik atau pilih konsumen"
                    data-testid="input-customer"
                    className="pl-9"
                  />
                </div>
                {showCustomerList && (
                  <div className="absolute z-20 mt-1 w-full bg-white border border-[var(--hotto-border)] rounded-md shadow-lg max-h-64 overflow-y-auto">
                    {filteredCustomers.map((c) => (
                      <button
                        key={c.name}
                        type="button"
                        onMouseDown={()=>{setCustomer(c.name); setShowCustomerList(false);}}
                        data-testid={`customer-opt-${c.name}`}
                        className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-[var(--hotto-border)]"
                      >
                        <div className="text-sm font-medium">{c.name}</div>
                        <div className="text-xs text-slate-500">{c.order_count}x order · {formatIDR(c.total_spent)} · sering: {c.favorite_names?.slice(0,2).join(", ") || "-"}</div>
                      </button>
                    ))}
                    <button
                      type="button"
                      onMouseDown={openNewCustomer}
                      data-testid="btn-new-customer-inline"
                      className="w-full text-left px-3 py-2.5 hover:bg-emerald-50 flex items-center gap-2 text-[var(--hotto-primary)] font-medium text-sm border-t border-[var(--hotto-border)]"
                    >
                      <UserPlus className="w-4 h-4"/>
                      {customer.trim() ? `+ Daftarkan "${customer.trim()}" sebagai konsumen baru` : "+ Tambah Konsumen Baru"}
                    </button>
                  </div>
                )}
              </div>

              {suggestions.length > 0 && (
                <div className="border border-emerald-200 bg-emerald-50 rounded-md p-3" data-testid="suggestions-box">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-3.5 h-3.5 text-[var(--hotto-primary)]"/>
                    <div className="text-xs font-semibold text-[var(--hotto-primary)]">Biasanya beli:</div>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {suggestions.slice(0, 5).map((s) => (
                      <button
                        key={s.product_id}
                        type="button"
                        onClick={()=>addByProductId(s.product_id)}
                        data-testid={`suggest-${s.product_id}`}
                        className="text-xs bg-white border border-emerald-300 hover:bg-emerald-100 text-emerald-800 rounded-full px-2.5 py-1 transition-colors"
                      >
                        + {s.name} <span className="text-emerald-600">({s.count}x)</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div><Label>Metode Bayar</Label>
                <Select value={payment} onValueChange={setPayment}>
                  <SelectTrigger data-testid="select-payment"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Tunai</SelectItem>
                    <SelectItem value="transfer">Transfer</SelectItem>
                    <SelectItem value="qris">QRIS</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-[var(--hotto-border)]">
                <span className="overline">Total</span>
                <span className="font-mono-num font-bold text-2xl text-[var(--hotto-primary)]">{formatIDR(total)}</span>
              </div>
              <Button onClick={checkout} disabled={cart.length===0} data-testid="btn-checkout" className="w-full bg-[var(--hotto-primary)] hover:bg-[var(--hotto-primary-hover)]">
                Proses Penjualan
              </Button>
            </div>
          </Card>
        </div>
      </div>

      {/* Dialog: Konsumen Baru */}
      <Dialog open={newCustOpen} onOpenChange={setNewCustOpen}>
        <DialogContent data-testid="dialog-new-customer">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><UserPlus className="w-5 h-5 text-[var(--hotto-primary)]"/>Konsumen Baru</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nama</Label><Input value={newCust.name} onChange={(e)=>setNewCust({...newCust,name:e.target.value})} data-testid="nc-name" autoFocus/></div>
            <div><Label>No. HP / WhatsApp</Label><Input value={newCust.phone} onChange={(e)=>setNewCust({...newCust,phone:e.target.value})} placeholder="08xxx..." data-testid="nc-phone"/></div>
            <div><Label>Alamat (opsional)</Label><Textarea rows={2} value={newCust.address} onChange={(e)=>setNewCust({...newCust,address:e.target.value})} data-testid="nc-address"/></div>
            <div><Label>Catatan (opsional)</Label><Input value={newCust.notes} onChange={(e)=>setNewCust({...newCust,notes:e.target.value})} placeholder="Preferensi, ulang tahun, dll"/></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setNewCustOpen(false)}>Batal</Button>
            <Button onClick={saveNewCustomer} data-testid="btn-save-new-customer" className="bg-[var(--hotto-primary)] hover:bg-[var(--hotto-primary-hover)]">
              Simpan & Pakai
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
